import {
  // DeleteOutlined,
  DownloadOutlined,
  // EditOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Switch, TableColumnsType } from "antd";
import moment from "moment";

import { ICountry, ILongTermIncentive, ILongTermPlan, IVersion } from "@types";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";
import { dateFormat, roles } from "@/constants";
import { formatNumberPlain } from "@/utils/formatNumberWithCommas";
import { BsTrash3 } from "react-icons/bs";
import { GoPencil } from "react-icons/go";

export const getColumns = ({
  removeLongTermIncentive,
  longTermIncentive_id,
  isDeleting,
  editLongTermIncentive,
  userRole,
}: {
  removeLongTermIncentive: (id: number) => Promise<void>;
  longTermIncentive_id: React.MutableRefObject<any>;
  isDeleting: boolean;
  editLongTermIncentive: (incentive: ILongTermIncentive) => void;
  userRole: string;
}): TableColumnsType<any> => [
    {
      title: "plan name",
      key: "name",
      dataIndex: "plan",
      width: 200,
      sorter: true,
      align: "center",
      render: (plan: ILongTermPlan) => plan?.name,
    },
    {
      title: "type of plan",
      dataIndex: "plan",
      key: "type",
      width: 250,
      sorter: true,
      align: "center",
      render: (plan: ILongTermPlan) => plan?.type?.name,
    },
    {
      title: "grade",
      dataIndex: "grade",
      key: "grade",
      width: 200,
      sorter: true,
      align: "center",
      render: (grade: any, record: any) => {
        return record?.all_plan_grades
          ? "All Grades"
          : grade || "";
      },
    },


    {
      title: "country",
      key: "country",
      dataIndex: "country",
      width: 200,
      sorter: true,
      align: "center",
      render: (country: ICountry | null, record: any) => {
        const isOnlyCompanyUser = roles.onlyCompany.includes(userRole);
        const isGlobal = record?.all_plan_countries; // assumes `is_global` exists in the full row
        return (
          <div className="text-wrap">
            {isOnlyCompanyUser
              ? typeof country == "string"
                ? country
                : country?.name
              : typeof country == "string"
                ? country
                : country?.name}
          </div>
        );
      },
    },


    {
      title: "function",
      dataIndex: "job_function",
      key: "job_function",
      width: 220,
      sorter: true,
      align: "center",
      render: (jobFunction: any) => (
        <div className="text-wrap">
          {Array.isArray(jobFunction)
            ? jobFunction
              .map((item: any) => item?.name)
              .filter(Boolean)
              .join(", ") || "-"
            : jobFunction?.name || "-"}
        </div>
      ),
    },

    {
      title: "sub function",
      dataIndex: "sub_job_function",
      key: "sub_job_function",
      width: 250,
      sorter: true,
      align: "center",
      render: (subJobFunction: any) => {
        let value = "-";

        if (typeof subJobFunction === "string") {
          value = subJobFunction;
        } else if (Array.isArray(subJobFunction)) {
          value =
            subJobFunction
              .map((item: any) =>
                typeof item === "string" ? item : item?.name,
              )
              .filter(Boolean)
              .join(", ") || "-";
        } else if (subJobFunction?.name) {
          value = subJobFunction.name;
        }

        return <div className="text-wrap">{value}</div>;
      },
    },

    {
      title: "currency",
      key: "currency",
      width: 250,
      sorter: true,
      align: "center",
      render: (_, record) => {
        return (
          record?.plan?.currency?.code ||
          record?.country?.currency?.code ||
          "-"
        );
      },
    },
    {
      title: "Grant Basis",
      dataIndex: "range_type",
      key: "range_type",
      width: 250,
      sorter: true,
      align: "center",
    },
    {
      title: "equity min",
      dataIndex: "equity_min",
      key: "equity_min",
      width: 250,
      sorter: true,
      align: "center",
      render: value => {
        const plainNumber = formatNumberPlain(value);
        return value;
      },
    },
    {
      title: "equity mid",
      dataIndex: "equity_mid",
      key: "equity_mid",
      width: 250,
      sorter: true,
      align: "center",
      render: value => {
        const plainNumber = formatNumberPlain(value);
        return plainNumber;
      },
    },
    {
      title: "equity max",
      dataIndex: "equity_max",
      key: "equity_max",
      width: 250,
      sorter: true,
      align: "center",
      render: value => {
        const plainNumber = formatNumberPlain(value);
        return value;
      },
    },
    {
      title: "year",
      dataIndex: "year",
      key: "year",
      sorter: true,
      width: 250,
    },
    ...(!checkPermission([
      permissions.DELETE_COMPANY_LTI,
      permissions.UPDATE_COMPANY_LTI,
    ])
      ? []
      : ([
        {
          title: "Actions",
          key: "actions",
          width: 160,
          fixed: "right",
          align: "center",
          align: "center",
          render: (incentive: ILongTermIncentive) => (
            <div className="flex items-center gap-2">
              {checkPermission(permissions.UPDATE_COMPANY_LTI) && (
                <div
                  onClick={() => editLongTermIncentive(incentive)}
                  className="table__action__btn table__action__btn--client"
                >
                  <GoPencil className="text-white mr-1 text-sm" /> Edit
                </div>
              )}
              {checkPermission(permissions.DELETE_COMPANY_LTI) && (
                <div
                  onClick={() => removeLongTermIncentive(incentive?.id)}
                  className="table__action__btn table__action__btn--delete"
                >
                  {isDeleting &&
                    incentive?.id === longTermIncentive_id?.current ? (
                    <LoadingOutlined color="#B62424" className="spinner" />
                  ) : (
                    <>
                      <BsTrash3 className="text-white textx mr-1" /> Delete
                    </>
                  )}
                </div>
              )}
            </div>
          ),
        },
      ] as any)),
  ];

export const getVersionsColumns = ({
  handleActive,
  versionId,
  download,
}: {
  handleActive: (id: number) => Promise<void>;
  versionId: number | null;
  download: (id?: number | undefined) => void;
}): TableColumnsType<any> => [
    {
      title: "created at",
      dataIndex: "created_at",
      key: "created_at",
      width: "35%",
      align: "center",
      render: (date: string) => <div>{moment(date).format(dateFormat)}</div>,
    },
    {
      title: "updated at",
      dataIndex: "updated_at",
      key: "updated_at",
      width: "35%",
      align: "center",
      render: (date: string) => <div>{moment(date).format(dateFormat)}</div>,
    },
    ...(!checkPermission(permissions.CREATE_COMPANY_LTI)
      ? []
      : ([
        {
          title: "Active",
          key: "active",
          width: "15%",
          align: "center",
          align: "center",
          render: (version: any) => (
            <Switch
              onChange={checked => {
                if (!checked) return;
                handleActive(version?.id);
              }}
              checked={
                versionId === version?.id ||
                (version?.is_active === "TRUE" && versionId === version?.id)
              }
              defaultChecked={version?.is_active === "TRUE"}
            />
          ),
        },
      ] as any)),
    {
      title: "Actions",
      key: "action",
      width: "15%",
      align: "center",
      align: "center",
      render: (version: IVersion) => (
        <div
          onClick={() => download(version?.id)}
          className="table__action__btn table__action__btn--download"
        >
          Download
        </div>
      ),
    },
  ];
